/**
 * Forms manager (CC_11-Revision — the missing UI over the D-Forms backend).
 * One dialog per list: shows the list's form views with their public /f/<id>
 * URL (copyable), creates new forms and edits/deletes existing ones. The field
 * editor mirrors the stored config shape (D-59): per column visible / required /
 * prefill_default, plus the public_submit switch (D-66, default OFF).
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { ryfeApi } from "../api";

import "./ryfe.scss";

import type {
  ListColumn,
  ListView,
  ListViewField,
  RelationOption,
} from "../api";

interface FieldDraft {
  column_id: string;
  name: string;
  type: string;
  visible: boolean;
  required: boolean;
  prefill: string;
  // R4 — nur bei Relations-Spalten: kuratierte Ziel-Optionen + Schalter.
  relationOptions: RelationOption[];
  relationMultiple: boolean;
  relationLiveSearch: boolean;
  // Ziel-Liste (aus der Spalten-config) — für die Kuratier-Suche.
  targetListId: string | null;
}

// Mehrfachauswahl aus der Kardinalität ableiten (Feld-Override gewinnt).
const deriveMultiple = (c: ListColumn): boolean => {
  const card =
    (c.config && (c.config as { cardinality?: string }).cardinality) || "1:n";
  return String(card).split(":")[1] === "n";
};

const draftFromColumns = (
  columns: ListColumn[],
  existing?: ListViewField[],
): FieldDraft[] => {
  const byId = new Map((existing || []).map((f) => [f.column_id, f]));
  return columns
    .filter((c) => c.type !== "uuid" && c.type !== "webhooks")
    .map((c) => {
      const f = byId.get(c.id);
      return {
        column_id: c.id,
        name: c.name,
        type: c.type,
        visible: f ? f.visible !== false : true,
        required: f ? f.required === true : false,
        prefill:
          f && f.prefill_default !== undefined && f.prefill_default !== null
            ? String(f.prefill_default)
            : "",
        relationOptions: Array.isArray(f?.relation_options)
          ? (f!.relation_options as RelationOption[])
          : [],
        relationMultiple:
          typeof f?.relation_multiple === "boolean"
            ? f!.relation_multiple!
            : deriveMultiple(c),
        relationLiveSearch: f?.relation_live_search === true,
        targetListId:
          (c.config && (c.config as { targetListId?: string }).targetListId) ||
          null,
      };
    });
};

export const FormsDialog = ({
  wsId,
  fileId,
  columns,
  onClose,
}: {
  wsId: string;
  fileId: string;
  columns: ListColumn[];
  onClose: () => void;
}) => {
  const [forms, setForms] = useState<ListView[]>([]);
  const [editing, setEditing] = useState<{
    view?: ListView; // absent = create
    name: string;
    publicSubmit: boolean;
    fields: FieldDraft[];
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const res = await ryfeApi.listListViews(wsId, fileId);
      setForms(res.views.filter((v) => v.type === "form"));
    } catch {
      setForms([]);
    }
  }, [wsId, fileId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const formUrl = (v: ListView) =>
    `${typeof window !== "undefined" ? window.location.origin : ""}/f/${
      v.config.form_id ?? ""
    }`;

  const save = async () => {
    if (!editing || !editing.name.trim() || busy) {
      return;
    }
    setBusy(true);
    try {
      const config = {
        public_submit: editing.publicSubmit,
        fields: editing.fields.map((f, i) => ({
          column_id: f.column_id,
          visible: f.visible,
          required: f.required,
          order: i,
          ...(f.prefill.trim() !== ""
            ? { prefill_default: f.prefill.trim() }
            : {}),
          // R4 — kuratierte Relations-Auswahl + Schalter nur für Relationen.
          ...(f.type === "relation"
            ? {
                relation_options: f.relationOptions,
                relation_multiple: f.relationMultiple,
                relation_live_search: f.relationLiveSearch,
              }
            : {}),
        })),
      };
      if (editing.view) {
        await ryfeApi.updateListView(wsId, fileId, editing.view.id, {
          name: editing.name.trim(),
          config,
        });
      } else {
        await ryfeApi.createListView(wsId, fileId, {
          type: "form",
          name: editing.name.trim(),
          config,
        });
      }
      setEditing(null);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (v: ListView) => {
    if (!window.confirm(`Formular „${v.name}" löschen?`)) {
      return;
    }
    await ryfeApi.deleteListView(wsId, fileId, v.id);
    await reload();
  };

  const copyUrl = async (v: ListView) => {
    const url = formUrl(v);
    try {
      await navigator.clipboard?.writeText(url);
      setCopied(v.id);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      window.prompt("Formular-URL kopieren:", url);
    }
  };

  const setField = (columnId: string, patch: Partial<FieldDraft>) =>
    setEditing((e) =>
      e
        ? {
            ...e,
            fields: e.fields.map((f) =>
              f.column_id === columnId ? { ...f, ...patch } : f,
            ),
          }
        : e,
    );

  return (
    <div
      className="ryfe-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
        <h2 className="ryfe-panel__title">Formulare</h2>

        {!editing && (
          <>
            <ul className="ryfe-formlist" data-testid="forms-list">
              {forms.map((v) => (
                <li key={v.id} className="ryfe-formlist__item">
                  <div className="ryfe-formlist__row">
                    <span className="ryfe-formlist__name">{v.name}</span>
                    <span className="ryfe-muted">
                      {v.config.public_submit ? "öffentlich" : "nur angemeldet"}
                    </span>
                    <span className="ryfe-topbar__spacer" />
                    <button
                      className="ryfe-btn ryfe-btn--ghost"
                      title="Formular-URL kopieren"
                      onClick={() => void copyUrl(v)}
                    >
                      {copied === v.id ? "✓ kopiert" : "URL kopieren"}
                    </button>
                    <button
                      className="ryfe-btn ryfe-btn--ghost"
                      title="Bearbeiten"
                      onClick={() =>
                        setEditing({
                          view: v,
                          name: v.name,
                          publicSubmit: v.config.public_submit === true,
                          fields: draftFromColumns(columns, v.config.fields),
                        })
                      }
                    >
                      ✎
                    </button>
                    <button
                      className="ryfe-btn ryfe-btn--ghost"
                      title="Löschen"
                      onClick={() => void remove(v)}
                    >
                      ×
                    </button>
                  </div>
                  <input
                    className="ryfe-field-input"
                    readOnly
                    value={formUrl(v)}
                    onFocus={(e) => e.currentTarget.select()}
                    aria-label={`URL von ${v.name}`}
                  />
                </li>
              ))}
              {forms.length === 0 && (
                <li className="ryfe-muted">
                  Noch kein Formular — mit „＋ Neues Formular" anlegen.
                </li>
              )}
            </ul>
            <button
              className="ryfe-btn ryfe-btn--primary"
              data-testid="forms-new"
              onClick={() =>
                setEditing({
                  name: "",
                  publicSubmit: false,
                  fields: draftFromColumns(columns),
                })
              }
            >
              ＋ Neues Formular
            </button>
          </>
        )}

        {editing && (
          <div data-testid="forms-editor">
            <label className="ryfe-field-label">
              Name
              <input
                className="ryfe-field-input"
                value={editing.name}
                autoFocus
                data-testid="forms-name"
                onChange={(e) =>
                  setEditing({ ...editing, name: e.target.value })
                }
              />
            </label>
            <label className="ryfe-check">
              <input
                type="checkbox"
                data-testid="forms-public"
                checked={editing.publicSubmit}
                onChange={(e) =>
                  setEditing({ ...editing, publicSubmit: e.target.checked })
                }
              />
              Öffentlich (ohne Anmeldung ausfüllbar — nur Absenden, kein
              Mitlesen)
            </label>

            <div className="ryfe-formfields">
              <div className="ryfe-formfields__head">
                <span>Feld</span>
                <span>sichtbar</span>
                <span>Pflicht</span>
                <span>Vorbelegung</span>
              </div>
              {editing.fields.map((f) => (
                <div key={f.column_id}>
                  <div className="ryfe-formfields__row">
                    <span className="ryfe-formfields__name">
                      {f.name}
                      {f.type === "relation" && (
                        <span className="ryfe-muted" title="Verknüpfung">
                          {" "}
                          (⇄)
                        </span>
                      )}
                    </span>
                    <input
                      type="checkbox"
                      aria-label={`${f.name} sichtbar`}
                      checked={f.visible}
                      onChange={(e) =>
                        setField(f.column_id, { visible: e.target.checked })
                      }
                    />
                    <input
                      type="checkbox"
                      aria-label={`${f.name} Pflicht`}
                      checked={f.required}
                      onChange={(e) =>
                        setField(f.column_id, { required: e.target.checked })
                      }
                    />
                    {f.type === "relation" ? (
                      <span className="ryfe-muted">
                        {f.relationOptions.length} Option(en)
                      </span>
                    ) : (
                      <input
                        className="ryfe-field-input"
                        aria-label={`${f.name} Vorbelegung`}
                        placeholder="—"
                        value={f.prefill}
                        onChange={(e) =>
                          setField(f.column_id, { prefill: e.target.value })
                        }
                      />
                    )}
                  </div>
                  {/* R4 — Kuratier-Panel: welche Ziel-Zeilen im Formular
                      wählbar sind (auch für anonyme Absender), plus Schalter. */}
                  {f.type === "relation" && (
                    <RelationCuration
                      wsId={wsId}
                      fileId={fileId}
                      field={f}
                      onChange={(patch) => setField(f.column_id, patch)}
                    />
                  )}
                </div>
              ))}
            </div>
            <p className="ryfe-muted" style={{ padding: 0 }}>
              Ein <b>unsichtbares</b> Feld mit Vorbelegung wird bei jeder
              Einsendung automatisch gesetzt (z.&nbsp;B. „Abteilung je Link").
              Bei <b>Verknüpfungen</b> legst du die auswählbaren Einträge unten
              fest — diese können auch anonyme Absender wählen, ohne die
              Ziel-Tabelle einzusehen.
            </p>
            <div className="ryfe-modal__actions">
              <span className="ryfe-topbar__spacer" />
              <button
                className="ryfe-btn ryfe-btn--ghost"
                onClick={() => setEditing(null)}
              >
                Abbrechen
              </button>
              <button
                className="ryfe-btn ryfe-btn--primary"
                disabled={!editing.name.trim() || busy}
                data-testid="forms-save"
                onClick={() => void save()}
              >
                {editing.view ? "Speichern" : "Anlegen"}
              </button>
            </div>
          </div>
        )}

        {!editing && (
          <div className="ryfe-modal__actions">
            <span className="ryfe-topbar__spacer" />
            <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
              Schließen
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * R4 — Kuratier-Panel für ein Relations-Formularfeld: der Ersteller sucht in der
 * Ziel-Liste (guard-gefiltert, er ist angemeldet mit Leserecht) und gibt gezielt
 * Einträge frei. Nur diese sind später im Formular wählbar — auch für anonyme
 * Absender, ohne dass die ganze Ziel-Tabelle einsehbar wird. Zusätzlich:
 * Mehrfachauswahl, optionale Live-Suche für angemeldete Absender, Vorauswahl.
 */
const RelationCuration = ({
  wsId,
  fileId,
  field,
  onChange,
}: {
  wsId: string;
  fileId: string;
  field: FieldDraft;
  onChange: (patch: Partial<FieldDraft>) => void;
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<RelationOption[]>([]);
  const [searching, setSearching] = useState(false);
  const reqRef = useRef(0);

  useEffect(() => {
    const seq = ++reqRef.current;
    setSearching(true);
    ryfeApi
      .relationSearch(wsId, fileId, field.column_id, { q: query, limit: 8 })
      .then((r) => {
        if (seq === reqRef.current) {
          setResults(r.items || []);
        }
      })
      .catch(() => {
        if (seq === reqRef.current) {
          setResults([]);
        }
      })
      .finally(() => {
        if (seq === reqRef.current) {
          setSearching(false);
        }
      });
  }, [wsId, fileId, field.column_id, query]);

  const chosen = field.relationOptions;
  const chosenIds = new Set(chosen.map((o) => o.row_id));
  const addOption = (o: RelationOption) => {
    if (chosenIds.has(o.row_id)) {
      return;
    }
    onChange({ relationOptions: [...chosen, o] });
  };
  const removeOption = (rowId: string) => {
    const next = chosen.filter((o) => o.row_id !== rowId);
    onChange({
      relationOptions: next,
      // Vorauswahl leeren, wenn ihr Ziel entfernt wurde.
      ...(field.prefill === rowId ? { prefill: "" } : {}),
    });
  };

  return (
    <div className="ryfe-relcuration">
      <div className="ryfe-relcuration__chosen">
        {chosen.length === 0 ? (
          <span className="ryfe-muted">Noch keine Einträge freigegeben.</span>
        ) : (
          chosen.map((o) => (
            <span key={o.row_id} className="ryfe-relchip">
              {o.display || o.row_id}
              <button
                type="button"
                aria-label={`${o.display || o.row_id} entfernen`}
                onClick={() => removeOption(o.row_id)}
              >
                ✕
              </button>
            </span>
          ))
        )}
      </div>
      <input
        className="ryfe-field-input"
        placeholder="Ziel-Einträge suchen und freigeben…"
        aria-label={`${field.name}: Einträge suchen`}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {(results.length > 0 || searching) && (
        <ul className="ryfe-relcuration__results">
          {results
            .filter((o) => !chosenIds.has(o.row_id))
            .map((o) => (
              <li key={o.row_id}>
                <button
                  type="button"
                  className="ryfe-btn ryfe-btn--ghost"
                  onClick={() => addOption(o)}
                >
                  ＋ {o.display || o.row_id}
                </button>
              </li>
            ))}
          {searching && <li className="ryfe-muted">sucht…</li>}
        </ul>
      )}
      <div className="ryfe-relcuration__opts">
        <label className="ryfe-check">
          <input
            type="checkbox"
            checked={field.relationMultiple}
            onChange={(e) => onChange({ relationMultiple: e.target.checked })}
          />
          Mehrfachauswahl
        </label>
        <label className="ryfe-check">
          <input
            type="checkbox"
            checked={field.relationLiveSearch}
            onChange={(e) => onChange({ relationLiveSearch: e.target.checked })}
          />
          Angemeldete dürfen frei in der Ziel-Liste suchen
        </label>
        {chosen.length > 0 && (
          <label className="ryfe-field-label">
            Vorauswahl
            <select
              className="ryfe-select"
              value={field.prefill}
              onChange={(e) => onChange({ prefill: e.target.value })}
            >
              <option value="">— keine —</option>
              {chosen.map((o) => (
                <option key={o.row_id} value={o.row_id}>
                  {o.display || o.row_id}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
    </div>
  );
};
